import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import MainURL from '../../MainURL';
import ImageURL from '../../ImageURL';

// 메인 첫 영역 슬라이드 — 관리자 > 메인 슬라이드 (server/routers/common/MainSlides.js)
export interface MainSlide {
  id: number;
  title: string;
  image: string;
  imageMobile: string;
  linkUrl: string;
}

const AUTO_MS = 5000;
const slideSrc = (file: string) => `${ImageURL}/images/mainslide/${encodeURIComponent(file)}`;

export default function MainSlider() {
  const navigate = useNavigate();
  const [slides, setSlides] = useState<MainSlide[]>([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef<number | null>(null);

  useEffect(() => {
    axios
      .get(`${MainURL}/mainslide/list`, { params: { platform: 'web' } })
      .then((res) => setSlides(res.data?.items || []))
      .catch(() => setSlides([]));
  }, []);

  const count = slides.length;
  const go = useCallback((next: number) => setIndex(((next % count) + count) % count), [count]);

  useEffect(() => {
    if (count < 2 || paused) return;
    const timer = window.setTimeout(() => go(index + 1), AUTO_MS);
    return () => window.clearTimeout(timer);
  }, [index, count, paused, go]);

  if (count === 0) return null;

  const open = (slide: MainSlide) => {
    const link = slide.linkUrl;
    if (!link) return;
    if (link.startsWith('/')) {
      window.scrollTo(0, 0);
      navigate(link);
    } else {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <section
      className="main__slider"
      aria-roledescription="carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
      }}
    >
      <div className="main__slider__track" style={{ transform: `translateX(-${index * 100}%)` }}>
        {slides.map((slide, i) => (
          <div
            key={slide.id}
            className={`main__slider__slide${slide.linkUrl ? ' is-link' : ''}`}
            aria-hidden={i !== index}
            onClick={() => open(slide)}
          >
            <picture>
              {slide.imageMobile && <source media="(max-width: 640px)" srcSet={slideSrc(slide.imageMobile)} />}
              <img src={slideSrc(slide.image)} alt={slide.title || '사역자모아'} draggable={false} />
            </picture>
          </div>
        ))}
      </div>

      {count > 1 && (
        <>
          <button type="button" className="main__slider__nav main__slider__nav--prev" aria-label="이전 슬라이드" onClick={() => go(index - 1)}>
            <FaChevronLeft />
          </button>
          <button type="button" className="main__slider__nav main__slider__nav--next" aria-label="다음 슬라이드" onClick={() => go(index + 1)}>
            <FaChevronRight />
          </button>
          <div className="main__slider__dots">
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                className={i === index ? 'is-active' : ''}
                aria-label={`${i + 1}번째 슬라이드`}
                onClick={() => go(i)}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
